import React, { useState, useEffect, useMemo } from 'react';
import { compatApi } from '../../utils/Intercepter';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useFilteredPagination } from '../../utils/PageUtil';
import { formatMonthDay } from '../../utils/dateUtils';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { useIsMobile } from '../../utils/responsive';

// ─── Sub-components ──────────────────────────────────────────────────────────

function Avatar({ name, size = 28 }) {
    const initial = (name || '?').trim().slice(0, 1);
    let h = 0;
    for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
    const c1 = `hsl(${h}, 55%, 62%)`;
    const c2 = `hsl(${(h + 60) % 360}, 55%, 70%)`;
    return (
        <div style={{
            width: size, height: size, borderRadius: '50%',
            background: `linear-gradient(135deg, ${c1}, ${c2})`,
            color: 'white', fontSize: size * 0.42, fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
        }}>{initial}</div>
    );
}

function RoleBadge({ role }) {
    const isMaster = role === 'MASTER';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center',
            fontSize: 10.5, fontWeight: 700,
            color: isMaster ? PURPLE : '#5d5b6e',
            background: isMaster ? hexToRgba(PURPLE, .12) : '#f4f3f9',
            border: isMaster ? `1px solid ${hexToRgba(PURPLE, .20)}` : '1px solid #ececf2',
            padding: '3px 8px', borderRadius: 6, letterSpacing: '.06em', whiteSpace: 'nowrap',
        }}>{role}</span>
    );
}

function ApprovalBadge({ approved }) {
    if (approved) {
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 11.5, fontWeight: 600, color: MINT,
                background: hexToRgba(MINT, .14),
                padding: '3px 9px', borderRadius: 999, lineHeight: 1.5, whiteSpace: 'nowrap',
            }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: MINT }} />
                승인완료
            </span>
        );
    }
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11.5, fontWeight: 600, color: '#d97757',
            background: 'rgba(217,119,87,.14)',
            padding: '3px 9px', borderRadius: 999, lineHeight: 1.5, whiteSpace: 'nowrap',
        }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#d97757' }} />
            미승인
        </span>
    );
}

function StatTile({ label, value, suffix, accent, icon, highlight }) {
    return (
        <div style={{
            background: 'white', borderRadius: 16, padding: '16px 18px',
            border: highlight ? `1.5px solid ${hexToRgba(accent, .35)}` : '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04)',
            display: 'flex', alignItems: 'center', gap: 14,
        }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: hexToRgba(accent, .12), color: accent,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{icon}</div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 500, letterSpacing: '.03em' }}>{label}</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#1f1d2e', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                    {value}
                    <span style={{ fontSize: 12, color: '#8b8a99', fontWeight: 500, marginLeft: 3 }}>{suffix}</span>
                </span>
            </div>
        </div>
    );
}

const thStyle = (align = 'center', sortable = false) => ({
    padding: '12px 12px',
    fontSize: 12, fontWeight: 600, color: '#5d5b6e',
    textAlign: align,
    borderBottom: '1px solid #ececf2',
    background: '#f7f6fb',
    whiteSpace: 'nowrap',
    letterSpacing: '.02em',
    cursor: sortable ? 'pointer' : 'default',
    userSelect: 'none',
});

const tdStyle = (align = 'left', muted = false) => ({
    padding: '14px 12px',
    textAlign: align,
    borderBottom: '1px solid #f4f3f9',
    fontSize: 12.5,
    color: muted ? '#5d5b6e' : '#3a3849',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
});

function pageBtn(disabled, active = false) {
    return {
        minWidth: 32, height: 32, borderRadius: 8,
        border: active ? 'none' : '1.5px solid #ececf2',
        background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : disabled ? '#fafaff' : 'white',
        color: active ? 'white' : disabled ? '#c7c5d2' : '#3a3849',
        fontSize: 13, fontWeight: active ? 600 : 500,
        cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
    };
}

const IconChevron = ({ width = 11, height = 11, style = {} }) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <path d="m9 18 6-6-6-6" />
    </svg>
);

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ReadUserList() {
    const isMobile = useIsMobile(768);
    const navigate = useNavigate();
    const [userList, setUserList] = useState([]);
    const [searchOption, setSearchOption] = useState('email');
    const [searchWord, setSearchWord] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);

    // 연봉협상일('MM-DD') 정렬용 키 — PageUtil 은 키 이름에 'Date'가 있으면 날짜로 변환해 정렬하는데
    // 연도 없는 'MM-DD'는 날짜 변환이 안 되므로 문자열 키로 따로 둔다
    const sortableUserList = useMemo(
        () => userList.map(u => ({ ...u, negotiationMonthDay: u.negotiationDate || '' })),
        [userList]
    );

    const {
        currentItems,
        currentPage,
        totalPages,
        handlePageChange,
        handleNextGroup,
        handlePrevGroup,
        applySort,
        sortConfig,
        updateOriginalData,
    } = useFilteredPagination(sortableUserList, null, 10, 5, { key: 'organizationName', direction: 'asc' });

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const counts = useMemo(() => {
        const c = { total: userList.length, approved: 0, pending: 0, left: 0 };
        userList.forEach(u => {
            if (u.approved) c.approved++;
            else c.pending++;
            if (u.leaveDate) c.left++;
        });
        return c;
    }, [userList]);

    const fetchData = async () => {
        try {
            const response = await compatApi.get('/admin/user/readUserList');
            if (response.ok) {
                setUserList(response.data);
                updateOriginalData(response.data);
            } else {
                alert('사용자 목록을 가져오는 데 실패했습니다.');
            }
        } catch {
            alert('오류가 발생했습니다.');
        }
    };

    useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = () => {
        if (searchOption !== 'approved' && searchWord.trim() === '') {
            fetchData();
            return;
        }
        compatApi.get(`/admin/user/find/${searchOption}/${searchWord ? searchWord : 'false'}`)
            .then(response => {
                if (response.ok) {
                    setUserList(response.data);
                    handlePageChange(1);
                } else {
                    alert('검색에 실패했습니다.');
                }
            }).catch(() => alert('오류가 발생했습니다.'));
    };

    const handleDeleteUser = async (userEmail, organizationCode) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            const response = await compatApi.del('/admin/user/deleteUser', { userEmail, organizationCode });
            const message = typeof response.data === 'string' ? response.data : '';
            if (response.ok) {
                alert('삭제되었습니다.');
                setUserList(prev => prev.filter(item => item.userEmail !== userEmail || item.organizationCode !== organizationCode));
            } else {
                alert(message || '삭제 중 오류가 발생했습니다.');
            }
        } catch {
            alert('네트워크 오류가 발생했습니다.');
        }
    };

    const searchOptionsMap = {
        'organizationName': '소속',
        'email': '이메일',
        'name': '이름',
        'approved': '미승인',
    };

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* 헤더 */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>관리자</span>
                        <IconChevron style={{ opacity: 0.6 }} />
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>전체 회원 목록</span>
                    </div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>전체 회원 목록</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>
                        전체 조직에 속한 모든 회원을 조회하고 관리합니다.
                    </p>
                </div>

                {/* 통계 타일 */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 16 }}>
                    <StatTile label="전체 회원" value={counts.total} suffix="명" accent={PURPLE}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5" /><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" /><circle cx="17" cy="7" r="2.5" /><path d="M22 18c0-2.5-2-4-4.5-4" /></svg>}
                    />
                    <StatTile label="승인 완료" value={counts.approved} suffix="명" accent={MINT}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4 12 14.01l-3-3" /></svg>}
                    />
                    <StatTile label="미승인" value={counts.pending} suffix="명" accent="#d97757"
                        highlight={counts.pending > 0}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>}
                    />
                    <StatTile label="퇴사자" value={counts.left} suffix="명" accent="#c44569"
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>}
                    />
                </div>

                {/* 검색 행 */}
                <div style={{
                    background: 'white', borderRadius: 16, padding: '16px 20px',
                    border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04)',
                    marginBottom: 16,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
                }}>
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setFilterOpen(!filterOpen)}
                            style={{
                                height: 38, padding: '0 14px', paddingRight: 30,
                                borderRadius: 10, background: '#fafaff',
                                border: '1.5px solid #ececf2',
                                fontSize: 13, color: '#3a3849', fontWeight: 500,
                                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                                display: 'inline-flex', alignItems: 'center', gap: 6, position: 'relative',
                            }}>
                            {searchOptionsMap[searchOption]}
                            <IconChevron style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%) rotate(90deg)', color: '#8b8a99' }} />
                        </button>
                        {filterOpen && (
                            <>
                                <div onClick={() => setFilterOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 6,
                                    background: 'white', borderRadius: 10,
                                    boxShadow: '0 8px 24px -8px rgba(60,50,120,.20)',
                                    border: '1px solid #ececf2', minWidth: 100, overflow: 'hidden',
                                }}>
                                    {Object.entries(searchOptionsMap).map(([val, label]) => (
                                        <button key={val} onClick={() => { setSearchOption(val); setFilterOpen(false); }} style={{
                                            display: 'block', width: '100%', textAlign: 'left',
                                            padding: '9px 14px', background: searchOption === val ? hexToRgba(PURPLE, .08) : 'white',
                                            border: 'none', color: searchOption === val ? PURPLE : '#3a3849',
                                            fontSize: 13, fontWeight: searchOption === val ? 600 : 500,
                                            cursor: 'pointer', fontFamily: 'inherit',
                                        }}>{label}</button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    <input
                        type="text"
                        value={searchWord}
                        disabled={searchOption === 'approved'}
                        onChange={(e) => setSearchWord(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        placeholder={searchOption === 'approved' ? '미승인 회원 전체 조회' : '검색어를 입력 하세요'}
                        style={{
                            height: 38, width: 280, borderRadius: 10, padding: '0 14px',
                            border: '1.5px solid #ececf2', background: searchOption === 'approved' ? '#f7f6fb' : 'white',
                            fontSize: 13, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
                            transition: 'border-color 140ms, box-shadow 140ms',
                        }}
                        onFocus={(e) => { if (searchOption !== 'approved') { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`; } }}
                        onBlur={(e) => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                    />

                    <button onClick={handleSearch} style={{
                        height: 38, padding: '0 22px', borderRadius: 10, border: 'none',
                        background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                        color: 'white', fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                        boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                        </svg>
                        검색
                    </button>
                </div>

                {/* 테이블 카드 */}
                <div style={{
                    background: 'white', borderRadius: 16,
                    border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden',
                }}>
                    {/* 카드 헤더 */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '16px 20px',
                        background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                        borderBottom: '1px solid #ececf2',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>회원 목록</h2>
                            <span style={{
                                fontSize: 11, fontWeight: 600,
                                background: 'white', color: '#5d5b6e',
                                padding: '2px 8px', borderRadius: 999, lineHeight: 1.45,
                                border: '1px solid #ececf2',
                            }}>총 {userList.length}건</span>
                        </div>
                    </div>

                    {/* 테이블 */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                            fontSize: 13, color: '#3a3849',
                        }}>
                            <thead>
                                <tr>
                                    <th style={thStyle('center')}>번호</th>
                                    <th style={thStyle('left', true)} onClick={() => applySort('organizationName')}>소속{getSortIcon('organizationName')}</th>
                                    <th style={thStyle('left', true)} onClick={() => applySort('userEmail')}>이메일{getSortIcon('userEmail')}</th>
                                    <th style={thStyle('left', true)} onClick={() => applySort('userName')}>성명{getSortIcon('userName')}</th>
                                    <th style={thStyle('center', true)} onClick={() => applySort('joinDate')}>입사일{getSortIcon('joinDate')}</th>
                                    <th style={thStyle('center', true)} onClick={() => applySort('negotiationMonthDay')}>연봉협상일{getSortIcon('negotiationMonthDay')}</th>
                                    <th style={thStyle('center', true)} onClick={() => applySort('leaveDate')}>퇴사일{getSortIcon('leaveDate')}</th>
                                    <th style={thStyle('center')}>자격증</th>
                                    <th style={thStyle('center', true)} onClick={() => applySort('userRole')}>권한{getSortIcon('userRole')}</th>
                                    <th style={thStyle('center', true)} onClick={() => applySort('lastLogin')}>마지막 로그인{getSortIcon('lastLogin')}</th>
                                    <th style={thStyle('center')}>승인</th>
                                    <th style={thStyle('center')}>기능</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? currentItems.map((user, idx) => (
                                    <tr key={`${user.userEmail}-${user.organizationCode}`}
                                        style={{ background: 'white', transition: 'background 100ms' }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <td style={tdStyle('center', true)}>
                                            <span style={{ color: '#8b8a99' }}>{(currentPage - 1) * 10 + idx + 1}</span>
                                        </td>
                                        <td style={tdStyle('left')}>
                                            <span style={{ fontSize: 12.5, color: '#3a3849', fontWeight: 500 }}>{user.organizationName}</span>
                                        </td>
                                        <td style={tdStyle('left')}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <Avatar name={user.userName} />
                                                <span style={{ color: '#1f1d2e', fontWeight: 500, fontSize: 13 }}>{user.userEmail}</span>
                                            </div>
                                        </td>
                                        <td style={tdStyle('left')}>
                                            <span style={{ color: '#1f1d2e', fontWeight: 500 }}>{user.userName}</span>
                                        </td>
                                        <td style={tdStyle('center', true)}>
                                            <span style={{ color: !user.joinDate ? '#c7c5d2' : '#5d5b6e' }}>
                                                {user.joinDate ? format(user.joinDate, 'yyyy-MM-dd') : '—'}
                                            </span>
                                        </td>
                                        <td style={tdStyle('center', true)}>
                                            <span style={{ color: !user.negotiationDate ? '#c7c5d2' : '#5d5b6e' }}>
                                                {user.negotiationDate ? formatMonthDay(user.negotiationDate) : '—'}
                                            </span>
                                        </td>
                                        <td style={tdStyle('center', true)}>
                                            <span style={{ color: !user.leaveDate ? '#c7c5d2' : '#c44569', fontWeight: !user.leaveDate ? 400 : 600 }}>
                                                {user.leaveDate ? format(user.leaveDate, 'yyyy-MM-dd') : '—'}
                                            </span>
                                        </td>
                                        <td style={tdStyle('center')}>
                                            {user.certificates
                                                ? <span style={{ fontSize: 11.5, color: '#5d5b6e', fontWeight: 500, background: '#f4f3f9', padding: '2px 8px', borderRadius: 6 }}>{user.certificates}</span>
                                                : <span style={{ color: '#c7c5d2' }}>—</span>
                                            }
                                        </td>
                                        <td style={tdStyle('center')}>
                                            <RoleBadge role={user.userRole} />
                                        </td>
                                        <td style={tdStyle('center', true)}>
                                            <span style={{ color: !user.lastLogin ? '#c7c5d2' : '#5d5b6e', fontSize: 12 }}>
                                                {user.lastLogin ? format(user.lastLogin, 'yyyy-MM-dd HH:mm:ss') : '—'}
                                            </span>
                                        </td>
                                        <td style={tdStyle('center')}>
                                            <ApprovalBadge approved={user.approved} />
                                        </td>
                                        <td style={tdStyle('center')}>
                                            <div style={{ display: 'inline-flex', gap: 6 }}>
                                                <button onClick={() => navigate('/admin/readUserInfo', { state: user })} style={{
                                                    height: 28, padding: '0 10px', borderRadius: 7,
                                                    background: 'white', border: '1px solid #ececf2',
                                                    color: '#5d5b6e', fontSize: 11.5, fontWeight: 600,
                                                    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                                                    transition: 'all 120ms',
                                                }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#fafaff'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                                                >상세 정보</button>
                                                <button onClick={() => handleDeleteUser(user.userEmail, user.organizationCode)} style={{
                                                    height: 28, padding: '0 10px', borderRadius: 7,
                                                    background: 'rgba(196,69,105,.10)', border: 'none',
                                                    color: '#c44569', fontSize: 11.5, fontWeight: 600,
                                                    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                                                }}>삭제</button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={12} style={{ padding: '48px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
                                            사용자 목록이 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 20px', borderTop: '1px solid #ececf2',
                        background: '#fafaff',
                    }}>
                        <span style={{ fontSize: 12, color: '#8b8a99' }}>전체 {userList.length}건</span>
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', gap: 4 }}>
                                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} style={pageBtn(currentPage === 1)}>«</button>
                                <button onClick={handlePrevGroup} disabled={currentPage === 1} style={pageBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(n => n >= Math.max(1, currentPage - 2) && n <= Math.min(totalPages, currentPage + 2))
                                    .map(n => (
                                        <button key={n} onClick={() => handlePageChange(n)} style={pageBtn(false, n === currentPage)}>
                                            {n}
                                        </button>
                                    ))}
                                <button onClick={handleNextGroup} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages)}>»</button>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
